import { cn } from "@/lib/utils";
import * as React from "react";
import { Check, Copy } from "lucide-react";

interface ColorSwatchProps {
  name: string;
  utility: string;
  swatchClassName: string;
  token: string;
}

function useIsDark(): boolean {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsDark(root.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

interface CopyableTokenProps {
  token: string;
}

function CopyableToken({ token }: CopyableTokenProps) {
  const [copied, setCopied] = React.useState(false);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy token ${token}`}
      className={[
        "group flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-1.5 py-1 font-mono text-[10px] transition-colors",
        "hover:bg-surface",
        "text-ink-display",
      ].join(" ")}
    >
      <span className="text-ink-muted">Token</span>
      <span className="flex items-center gap-1.5">
        <span>{token}</span>
        {copied ? (
          <Check className="h-3 w-3 text-accent" aria-hidden />
        ) : (
          <Copy
            className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          />
        )}
      </span>
    </button>
  );
}

export function ColorSwatch({
  name,
  utility,
  swatchClassName,
  token,
}: ColorSwatchProps) {
  useIsDark();

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn("aspect-[2/1] rounded-md border border-hairline", swatchClassName)}
        aria-label={`${name} preview`}
      />
      <div className="text-xs">
        <div className="font-medium text-ink-display">{name}</div>
        <div className="font-mono text-ink-muted">{utility}</div>
        <div className="mt-1 flex flex-col gap-0.5">
          <CopyableToken token={token} />
        </div>
      </div>
    </div>
  );
}
